import { useGetVaDistributorMetricsQuery } from "../../app/vaApi/vaApi";
import { extractVaMetricsFromResult } from "../../helpers/vaMetricsUtils";
import {
  DISTRIBUTOR_METRICS_DEFAULT_FILTER,
  DISTRIBUTOR_METRICS_EMPTY_LABEL,
} from "../../helpers/metricsPeriod";
import MetricsPeriodFilter from "./MetricsPeriodFilter";
import Button from "../shared/buttons/button";
import { shouldSkipAuthedQuery } from "../../helpers/authSession";

const METRICS_CONFIG = [
  { key: "totalRevenue", label: "Total Revenue" },
  { key: "totalCommission", label: "Total Commission" },
  { key: "pendingPayout", label: "Pending Payout" },
  { key: "paidOut", label: "Paid Out" },
  { key: "returnsDeduction", label: "Returns Deduction", negative: true },
  { key: "ordersReceived", label: "Orders received", isUnits: true },
  { key: "ordersShipped", label: "Orders shipped", isUnits: true },
];

const EMPTY_METRICS = {
  totalRevenue: 0,
  totalCommission: 0,
  pendingPayout: 0,
  paidOut: 0,
  returnsDeduction: 0,
  ordersReceived: 0,
  ordersShipped: 0,
};

function formatCurrency(value) {
  const num = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function formatUnits(value) {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

function MetricsSkeletonCards() {
  return METRICS_CONFIG.map((metric) => (
    <div
      key={metric.key}
      className="distributor-metric distributor-metrics-card distributor-metric--skeleton"
    >
      <div className="distributor-metric__skeleton-label" />
      <div className="distributor-metric__skeleton-value" />
    </div>
  ));
}

function MetricsValueCards({ metrics }) {
  return METRICS_CONFIG.map((metric) => {
    const value = metrics[metric.key];
    const display = metric.isUnits
      ? formatUnits(value)
      : formatCurrency(value);

    return (
      <div
        key={metric.key}
        className="distributor-metric distributor-metrics-card"
      >
        <span className="distributor-metric__label">{metric.label}</span>
        <span
          className={`distributor-metric__value ${
            metric.negative ? "distributor-metric__value--negative" : ""
          }`}
        >
          {display}
        </span>
      </div>
    );
  });
}

export default function DistributorMetricsBar({
  filter = DISTRIBUTOR_METRICS_DEFAULT_FILTER,
  onFilterChange,
}) {
  const skip = shouldSkipAuthedQuery();
  const { data, isLoading, isError, refetch } =
    useGetVaDistributorMetricsQuery(filter, { skip });

  const metrics = extractVaMetricsFromResult(data) ?? EMPTY_METRICS;
  const showError = isError && !skip;

  return (
    <div className="distributor-metrics-bar">
      <div className="distributor-metrics-bar__metrics">
        <div className="distributor-metrics-bar__filter distributor-metrics-card">
          <MetricsPeriodFilter
            value={filter}
            onChange={onFilterChange}
            emptyLabel={DISTRIBUTOR_METRICS_EMPTY_LABEL}
          />
        </div>

        {showError ? (
          <div className="distributor-metrics-bar__error distributor-metrics-card">
            <span className="text-danger">Failed to load metrics.</span>
            <Button label="Retry" className="btn-grey" onClick={refetch} />
          </div>
        ) : isLoading || skip ? (
          <MetricsSkeletonCards />
        ) : (
          <MetricsValueCards metrics={metrics} />
        )}
      </div>
    </div>
  );
}
